import type { UserId } from '@hprofits/shared';
import type { UseQueryReturnType } from '@tanstack/vue-query';
import { useQuery } from '@tanstack/vue-query';
import { MaybeRefOrGetter, Ref, toValue } from 'vue';
import { computed } from 'vue';

import type { LegacyAuthApi } from '@/composables/api/useLegacyCreativeApi';
import { requireLegacyApi } from '@/composables/api/useLegacyCreativeApi';
import { requestAsOwner } from '@/composables/auth/useOwnerAuth';

import { OwnerProfileResponseSchema } from './ownerProfile.schema';

// Owner profile grants are near-static for the session — long staleTime avoids
// redundant disguise-token fetches. No oRPC contract; uses the legacy auth client.
const OWNER_GRANTS_STALE_TIME_MS = 300_000;

export type UseLegacyOwnerGrantsQueryOptions = {
  authApi: LegacyAuthApi | undefined;
  ownerId: MaybeRefOrGetter<UserId | null>;
  enabled: Readonly<Ref<boolean>>;
};

export type UseLegacyOwnerGrantsQueryReturn = UseQueryReturnType<ReadonlySet<number>, Error>;

export const useLegacyOwnerGrantsQuery = (
  options: UseLegacyOwnerGrantsQueryOptions,
): UseLegacyOwnerGrantsQueryReturn => {
  const queryKey = computed(() => ['owner', 'grants', toValue(options.ownerId)] as const);

  const query = useQuery({
    queryKey,
    enabled: options.enabled,
    staleTime: OWNER_GRANTS_STALE_TIME_MS,
    queryFn: async (): Promise<ReadonlySet<number>> => {
      const api = requireLegacyApi(options.authApi, 'Legacy auth API');
      const ownerId = toValue(options.ownerId);
      if (ownerId == null) {
        return new Set<number>();
      }
      const raw = await requestAsOwner(api, ownerId, 'get', '/profile');
      const parsed = OwnerProfileResponseSchema.parse(raw);
      return new Set(parsed.grants ?? []);
    },
  });

  return query;
};
